import { useEffect, useRef, type ReactNode } from "react";

/** Fades a block in once it scrolls into view. CSS-only motion. */
export function Reveal({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      el?.classList.add("is-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            el.classList.add("is-in");
            io.disconnect();
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    // Backstop: never leave content stranded at opacity 0 if the observer
    // is slow to fire or never does (a hidden tab, a capture, a flaky
    // callback). The scroll reveal wins in the normal case, long before this.
    const failsafe = window.setTimeout(() => el.classList.add("is-in"), 1400);
    return () => {
      io.disconnect();
      window.clearTimeout(failsafe);
    };
  }, []);
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
}
